import { PillTone } from '../components/DashboardPrimitives'
import { PayoutStatusFilter } from './Constants'

export type PayoutBadgeStatus =
  | 'awaitingConfirmation'
  | 'pending'
  | 'complete'
  | 'expired'
  | 'processing'
  | 'inReview'
  | 'failed'

// A payout awaiting confirmation was never sent: a quote left behind on the
// Send page, or an API draft nobody confirmed. One the merchant did send
// (autoConfirm) that still waits there is waiting on its recipients' checks.
const WAITING_TO_SEND_LABEL = 'Waiting to send'
const WAITING_TO_SEND_DESCRIPTION =
  'Sent, and waiting for its recipients to clear their checks. It goes out on its own once they do.'

const payoutStatusLabels: Record<string, string> = {
  AwaitingConfirmation: 'Not sent',
  Pending: 'Pending',
  Processing: 'Processing',
  Complete: 'Completed',
  Expired: 'Expired',
  InReview: 'In review',
  Converting: 'Converting funding',
  Failed: 'Failed'
}

const payoutStatusDescriptions: Record<string, string> = {
  AwaitingConfirmation: 'Nothing has moved. Confirm it to send it, or leave it and it expires on its own.',
  Pending: 'Accepted and waiting for funding or banking rail submission.',
  Processing: 'Submitted to the payout rail and awaiting final confirmation.',
  Complete: 'Paid out successfully.',
  Expired: 'Confirmation window expired before funds moved.',
  InReview: 'Held for a compliance check on the recipient.',
  Converting: 'Your funding balance is being converted. The payout goes out as soon as the conversion lands, usually within a minute.',
  Failed: 'Payout failed and needs review.'
}

const payoutBadgeStatuses: Record<string, PayoutBadgeStatus> = {
  AwaitingConfirmation: 'awaitingConfirmation',
  Pending: 'pending',
  Processing: 'processing',
  Complete: 'complete',
  Expired: 'expired',
  InReview: 'inReview',
  Converting: 'processing',
  Failed: 'failed'
}

export const payoutStatusOptions: PayoutStatusFilter[] = [
  PayoutStatusFilter.AwaitingConfirmation,
  PayoutStatusFilter.Pending,
  PayoutStatusFilter.Processing,
  PayoutStatusFilter.Complete,
  PayoutStatusFilter.Expired,
  PayoutStatusFilter.InReview,
  PayoutStatusFilter.Converting,
  PayoutStatusFilter.Failed
]

const waitingToSend = (status: string, autoConfirm?: boolean): boolean =>
  status === 'AwaitingConfirmation' && autoConfirm === true

/**
 * A payout row that never moved money: a quote left behind on the Send page
 * or an API draft nobody confirmed, still open or already expired. The
 * activity feeds list what happened, so they leave these out; the payout
 * history keeps them, where a draft can still be confirmed.
 */
export const isUnsentQuote = (payout: { status: string, autoConfirm?: boolean, confirmedAt?: string | null }): boolean =>
  (payout.status === 'AwaitingConfirmation' && payout.autoConfirm !== true) ||
  (payout.status === 'Expired' && !payout.confirmedAt)

export const payoutStatusLabel = (status: string, autoConfirm?: boolean): string =>
  waitingToSend(status, autoConfirm) ? WAITING_TO_SEND_LABEL : (payoutStatusLabels[status] ?? status)

export const payoutStatusTone = (status: string, autoConfirm?: boolean): PillTone => {
  switch (status) {
  case 'Complete':
    return 'green'
  case 'Failed':
  case 'Expired':
    return 'red'
  case 'Processing':
  case 'Pending':
    return 'blue'
  case 'AwaitingConfirmation':
    return autoConfirm ? 'amber' : 'neutral'
  default:
    return 'amber'
  }
}

export const payoutStatusDescription = (status: string, autoConfirm?: boolean): string =>
  waitingToSend(status, autoConfirm)
    ? WAITING_TO_SEND_DESCRIPTION
    : (payoutStatusDescriptions[status] ?? 'Status update unavailable.')

export const payoutBadgeStatus = (status: string): PayoutBadgeStatus =>
  payoutBadgeStatuses[status] ?? 'pending'
